import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const R = 10;
const C = 2 * Math.PI * R;

export function SyncStatusBar({
  onManualRefresh,
  autoRefreshProgress,
  connectedTool,
  pulseKey,
}) {
  const [now, setNow] = useState(Date.now());
  const [flash, setFlash] = useState(false);
  const prevPulse = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (prevPulse.current === null) {
      prevPulse.current = pulseKey;
      return;
    }
    if (pulseKey != null && pulseKey !== prevPulse.current) {
      prevPulse.current = pulseKey;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 700);
      return () => clearTimeout(t);
    }
  }, [pulseKey]);

  const last = autoRefreshProgress?.lastRefreshAt;
  const intervalMs = autoRefreshProgress?.intervalMs || 60000;
  const ago =
    last != null
      ? `${Math.max(0, Math.floor((now - last) / 1000))}s ago`
      : '—';

  const elapsed = last != null ? Math.min(intervalMs, Math.max(0, now - last)) : 0;
  const progress = intervalMs > 0 ? elapsed / intervalMs : 0;
  const dashOffset = C * (1 - progress);

  const toolLabel =
    connectedTool === 'zoho'
      ? 'Zoho Books'
      : connectedTool === 'tally'
        ? 'Tally'
        : null;

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-2 text-xs text-slate-600 transition-shadow duration-300 ${
        flash ? 'ring-2 ring-blue-200/80' : ''
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">
          Showing reconciled data
        </span>
        {toolLabel ? (
          <span className="hidden sm:inline text-slate-500">
            from <span className="font-semibold text-slate-700">{toolLabel}</span>
          </span>
        ) : null}
        <span>Dashboard refreshed {ago}</span>
        {intervalMs ? (
          <span className="hidden sm:inline-flex sm:items-center sm:gap-1.5 text-slate-400">
            <span className="inline-flex items-center gap-1">
              <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden>
                <circle
                  cx="13"
                  cy="13"
                  r={R}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className="text-slate-200"
                />
                <circle
                  cx="13"
                  cy="13"
                  r={R}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeDasharray={C}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  transform="rotate(-90 13 13)"
                  className="text-blue-500"
                />
              </svg>
              <span>Next auto-refresh</span>
            </span>
            <span className="text-slate-500">({intervalMs / 1000}s)</span>
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onManualRefresh}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-100"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Refresh data
      </button>
    </div>
  );
}
